import { create } from "zustand"

import { TRANSFER_FILTERABLE_STATUSES, TransferSyncStatusFilter } from "../constants"
import type { TransferSearchParams } from "../schemas/transfer"

import { GetTransferListDocument, type GetTransferListQuery, type Transfer } from "@/graphql/codegen/graphql"
import { createSearchStore } from "@/stores/createSearchStore"

const deduplicateTransfers = (newData: Transfer[], existingData: Transfer[]): Transfer[] => {
  const uniqueIds = new Set(existingData.map((item) => item.transferId))
  return [...existingData, ...newData.filter((item) => !uniqueIds.has(item.transferId))]
}

export const useTransferSearchStore = create(
  createSearchStore<Transfer, TransferSearchParams, GetTransferListQuery>({
    documentNode: GetTransferListDocument,
    deduplicateData: deduplicateTransfers,
    getQueryInput: (filters: TransferSearchParams, offset: number, limit: number) => {
      const {
        field,
        order,
        query,
        types,
        statuses,
        sourceWarehouseAreaIds,
        targetWarehouseAreaIds,
        createdByUserIds,
        createdOnFrom,
        createdOnTo,
      } = filters
      // TODO(AK-6818): Remove this filter once the backend accepts AS400 sync statuses, since `TransferListFilter`
      // rejects any value outside `TransferStatus`.
      const transferStatuses = statuses?.filter(
        (status) => !Object.values<string>(TransferSyncStatusFilter).includes(status)
      )

      // TODO(AK-6818): Send the AS400 sync statuses (`statuses` values from `TransferSyncStatusFilter`) and
      // `productCostCompleted` once `TransferListFilter` supports them. Product Cost Completed maps to a boolean
      // when Yes or No is selected, and is omitted for All (unset). Selecting either value must exclude
      // intra-warehouse transfers on the backend.
      return {
        offset,
        limit,
        sort: field && order ? { field, order } : undefined,
        filter: {
          query,
          types: types?.length ? types : undefined,
          statuses: transferStatuses?.length ? transferStatuses : TRANSFER_FILTERABLE_STATUSES,
          sourceWarehouseAreaIds: sourceWarehouseAreaIds?.length ? sourceWarehouseAreaIds : undefined,
          targetWarehouseAreaIds: targetWarehouseAreaIds?.length ? targetWarehouseAreaIds : undefined,
          createdByUserIds: createdByUserIds?.length ? createdByUserIds : undefined,
          createdOnFrom: createdOnFrom ?? undefined,
          createdOnTo: createdOnTo ?? undefined,
        },
      }
    },
    getQueryVariables: (queryInput) => ({ input: queryInput }),
    extractDataFromResponse: (data) => ({
      __typename: data.transfer.list.__typename,
      data: data.transfer.list.__typename === "TransferListSuccess" ? (data.transfer.list.items as Transfer[]) : [],
      error: data.transfer.list.__typename === "TransferListFailure" ? data.transfer.list.error : undefined,
    }),
    hasMoreData: (response, state) => response.data.length === state.limit,
  })
)
